import { merge } from "node-diff3";

/**
 * Files outside any step directory (the workflow `README.md`, plus legacy
 * root files older commits still carry) travel through the merge as a
 * pseudo-step under this dir. Unlike real steps they are independent files,
 * not one entity, so they never merge step-wholesale — always per-file.
 */
export const ROOT_DIR = "";

type StepFile = { path: string; content: string };

export type CommitStep = {
  dir: string;
  files: StepFile[];
};

type FileConflict = {
  dir: string;
  path: string;
};

type DeleteConflict = {
  dir: string;
  deletedBy: "main" | "branch";
};

type MergedStep = {
  dir: string;
  mergedFiles: StepFile[];
};

type MergeResult = {
  /** Steps to keep as-is from one side */
  keepSteps: CommitStep[];
  /** Steps that need new file content because files were merged */
  mergedSteps: MergedStep[];
  /** Files where diff3 couldn’t auto-resolve */
  fileConflicts: FileConflict[];
  /** One side deleted, other modified */
  deleteConflicts: DeleteConflict[];
};

function filesEqual(a: StepFile[], b: StepFile[]): boolean {
  if (a.length !== b.length) return false;
  const aMap = new Map(a.map((f) => [f.path, f.content]));
  for (const bf of b) {
    if (aMap.get(bf.path) !== bf.content) return false;
  }
  return true;
}

/**
 * Three-way merge of workflow steps, analogous to `git merge`.
 *
 * Compares base (fork point), main (current live), and branch (user’s work)
 * by directory name. Uses node-diff3 for file-level merging when both sides
 * modified the same step. Metadata files (.color, .command, etc.) merge
 * naturally since they’re individual files.
 */
export function threeWayMerge(
  baseSteps: CommitStep[],
  mainSteps: CommitStep[],
  branchSteps: CommitStep[]
): MergeResult {
  const baseMap = new Map(baseSteps.map((s) => [s.dir, s]));
  const mainMap = new Map(mainSteps.map((s) => [s.dir, s]));
  const branchMap = new Map(branchSteps.map((s) => [s.dir, s]));

  const allDirs = new Set([
    ...baseMap.keys(),
    ...mainMap.keys(),
    ...branchMap.keys(),
  ]);

  const keepSteps: CommitStep[] = [];
  const mergedSteps: MergedStep[] = [];
  const fileConflicts: FileConflict[] = [];
  const deleteConflicts: DeleteConflict[] = [];

  for (const dir of allDirs) {
    const base = baseMap.get(dir);
    const main = mainMap.get(dir);
    const branch = branchMap.get(dir);

    if (dir === ROOT_DIR) {
      const empty: CommitStep = { dir: ROOT_DIR, files: [] };
      const rootResult = mergeStepFiles(
        ROOT_DIR,
        base ?? empty,
        main ?? empty,
        branch ?? empty
      );
      fileConflicts.push(...rootResult.conflicts);
      if (rootResult.merged) {
        mergedSteps.push(rootResult.merged);
      } else if (branch) {
        keepSteps.push(branch);
      }
      continue;
    }

    if (!base) {
      // Not in base: added by one or both sides
      if (main && !branch) {
        keepSteps.push(main);
      } else if (branch && !main) {
        keepSteps.push(branch);
      } else if (main && branch) {
        const result = mergeStepFiles(dir, { dir, files: [] }, main, branch);
        fileConflicts.push(...result.conflicts);
        if (result.merged) mergedSteps.push(result.merged);
        else keepSteps.push(branch);
      }
      continue;
    }

    if (!main && !branch) {
      // Both deleted — omit
      continue;
    }

    if (!main) {
      // Main deleted this step
      const branchChanged = !filesEqual(base.files, branch!.files);
      if (branchChanged) {
        // Conflict: main deleted, branch modified — keep branch version
        deleteConflicts.push({ dir, deletedBy: "main" });
        keepSteps.push(branch!);
      }
      // If branch didn’t change it, honor main’s deletion (omit)
      continue;
    }

    if (!branch) {
      // Branch deleted this step
      const mainChanged = !filesEqual(base.files, main.files);
      if (mainChanged) {
        // Conflict: branch deleted, main modified — keep main version
        deleteConflicts.push({ dir, deletedBy: "branch" });
        keepSteps.push(main);
      }
      // If main didn’t change it, honor branch’s deletion (omit)
      continue;
    }

    // Both exist. Check if either side changed.
    const mainChanged = !filesEqual(base.files, main.files);
    const branchChanged = !filesEqual(base.files, branch.files);

    if (!mainChanged && !branchChanged) {
      // Neither changed — keep main’s version
      keepSteps.push(main);
    } else if (mainChanged && !branchChanged) {
      // Only main changed — take main’s version
      keepSteps.push(main);
    } else if (!mainChanged && branchChanged) {
      // Only branch changed — take branch’s version
      keepSteps.push(branch);
    } else {
      // Both changed — file-level merge
      const result = mergeStepFiles(dir, base, main, branch);
      if (result.conflicts.length > 0) {
        fileConflicts.push(...result.conflicts);
      }
      if (result.merged) {
        mergedSteps.push(result.merged);
      } else {
        // All files were identical or resolved — keep branch’s version
        keepSteps.push(branch);
      }
    }
  }

  return { keepSteps, mergedSteps, fileConflicts, deleteConflicts };
}

function mergeStepFiles(
  dir: string,
  base: CommitStep,
  main: CommitStep,
  branch: CommitStep
): { merged: MergedStep | null; conflicts: FileConflict[] } {
  const baseFiles = new Map(base.files.map((f) => [f.path, f.content]));
  const mainFiles = new Map(main.files.map((f) => [f.path, f.content]));
  const branchFiles = new Map(branch.files.map((f) => [f.path, f.content]));

  const allPaths = new Set([
    ...baseFiles.keys(),
    ...mainFiles.keys(),
    ...branchFiles.keys(),
  ]);

  const mergedFiles: StepFile[] = [];
  const conflicts: FileConflict[] = [];
  let anyFileMerged = false;

  for (const path of allPaths) {
    const baseContent = baseFiles.get(path);
    const mainContent = mainFiles.get(path);
    const branchContent = branchFiles.get(path);

    if (baseContent === undefined) {
      // File added by one or both sides
      if (mainContent !== undefined && branchContent === undefined) {
        mergedFiles.push({ path, content: mainContent });
        anyFileMerged = true;
      } else if (branchContent !== undefined && mainContent === undefined) {
        mergedFiles.push({ path, content: branchContent });
        anyFileMerged = true;
      } else if (mainContent !== undefined && branchContent !== undefined) {
        if (mainContent === branchContent) {
          mergedFiles.push({ path, content: branchContent });
        } else {
          // Both added different content — merge with conflict markers
          const result = mergeText("", mainContent, branchContent);
          if (result.conflict) conflicts.push({ dir, path });
          mergedFiles.push({ path, content: result.text });
          anyFileMerged = true;
        }
      }
      continue;
    }

    if (mainContent === undefined && branchContent === undefined) {
      // Both deleted — omit file
      anyFileMerged = true;
      continue;
    }

    if (mainContent === undefined) {
      // Main deleted, branch has it
      if (branchContent === baseContent) {
        // Branch didn’t change — honor main’s deletion
        anyFileMerged = true;
      } else {
        // Branch modified, main deleted — conflict; keep branch’s version
        conflicts.push({ dir, path });
        mergedFiles.push({ path, content: branchContent! });
        anyFileMerged = true;
      }
      continue;
    }

    if (branchContent === undefined) {
      // Branch deleted, main has it
      if (mainContent === baseContent) {
        // Main didn’t change — honor branch’s deletion
        anyFileMerged = true;
      } else {
        // Main modified, branch deleted — conflict; keep main’s version
        conflicts.push({ dir, path });
        mergedFiles.push({ path, content: mainContent });
        anyFileMerged = true;
      }
      continue;
    }

    // Both sides have the file
    const mainChanged = mainContent !== baseContent;
    const branchChanged = branchContent !== baseContent;

    if (!mainChanged && !branchChanged) {
      mergedFiles.push({ path, content: baseContent });
    } else if (mainChanged && !branchChanged) {
      mergedFiles.push({ path, content: mainContent });
      anyFileMerged = true;
    } else if (!mainChanged && branchChanged) {
      mergedFiles.push({ path, content: branchContent });
    } else {
      // Both changed — three-way text merge
      if (mainContent === branchContent) {
        // Same change on both sides — no conflict
        mergedFiles.push({ path, content: branchContent });
      } else {
        const result = mergeText(baseContent, mainContent, branchContent);
        if (result.conflict) {
          conflicts.push({ dir, path });
        }
        mergedFiles.push({ path, content: result.text });
        anyFileMerged = true;
      }
    }
  }

  if (!anyFileMerged) return { merged: null, conflicts };

  return {
    merged: { dir, mergedFiles },
    conflicts,
  };
}

// Matches any git-style marker line, not only 3B’s own labels, so markers
// arriving through git sync are caught too.
export function hasConflictMarkers(content: string): boolean {
  return /^<{7} /m.test(content);
}

type ConflictSide = "ours" | "theirs" | "both";

function resolveConflictMarkers(content: string, keep: ConflictSide): string {
  const kept: string[] = [];
  let section: "outside" | "ours" | "base" | "theirs" = "outside";
  for (const line of content.split("\n")) {
    if (section === "outside" && /^<{7} /.test(line)) section = "ours";
    else if (section === "ours" && /^\|{7}/.test(line)) section = "base";
    else if ((section === "ours" || section === "base") && /^={7}$/.test(line))
      section = "theirs";
    else if (section === "theirs" && /^>{7} /.test(line)) section = "outside";
    else if (
      section === "outside" ||
      (section === "ours" && keep !== "theirs") ||
      (section === "theirs" && keep !== "ours")
    )
      kept.push(line);
  }
  // Unbalanced markers are hand-mangled content, not merge output; return it
  // untouched so parsing fails loudly instead of truncating silently.
  return section === "outside" ? kept.join("\n") : content;
}

export function keepOurs(content: string): string {
  return resolveConflictMarkers(content, "ours");
}

export function keepTheirs(content: string): string {
  return resolveConflictMarkers(content, "theirs");
}

export function keepBoth(content: string): string {
  return resolveConflictMarkers(content, "both");
}

/**
 * Three-way text merge using node-diff3. Returns merged text with git-style
 * conflict markers embedded when overlapping changes exist.
 */
function mergeText(
  base: string,
  main: string,
  branch: string
): { conflict: boolean; text: string } {
  // merge splits raw strings on /\s+/, tokenising by whitespace and
  // destroying the file’s line structure. Split into lines ourselves so the
  // merge operates line-by-line, then rejoin.
  const result = merge(branch.split("\n"), base.split("\n"), main.split("\n"), {
    label: { a: "your branch", b: "main" },
  });
  return { conflict: result.conflict, text: result.result.join("\n") };
}
